/**
 * Отдельный vuex модуль для взаимодействия по analytics/container_monitoring-API
 */

import {ANALYTICS, AnalyticSettings} from "@/store/analytics/helpers.js";
import makeMessage from "@/store/analytics/analyticMessage.js";

// actions для container_monitoring. Вызов действия начинать с "analytics/containerMonitorings/"
export const ACTION_LOAD_CONTAINER_MONITORINGS_SETTINGS = "LOAD_CONTAINER_MONITORINGS_SETTINGS";
export const ACTION_LOAD_CONTAINER_MONITORING_SETTINGS = "LOAD_CONTAINER_MONITORING_SETTINGS";
export const ACTION_LOAD_CONTAINER_MONITORING_EVENTS = "LOAD_CONTAINER_MONITORING_EVENTS";
export const ACTION_LOAD_CONTAINER_MONITORING_EVENT = "LOAD_CONTAINER_MONITORING_EVENT";
export const ACTION_LOAD_CONTAINER_MONITORING_REPORT = "LOAD_CONTAINER_MONITORING_REPORT";

/**
 * Класс для представления настроек аналитики доступа к мусоркам.
 */
export class ContainerMonitoringSettings extends AnalyticSettings {
  get analyticType() {
    return ANALYTICS.container_monitoring;
  }
}

export default {
  namespaced: true,
  state: {},
  mutations: {},
  actions: {
    /**
     * Загрузка списка настроек аналитик доступа к мусоркам.
     *
     * @param {Object} context
     * @param {Array.<String>} camerasNumbers
     * @return {Promise.<Array.<ContainerMonitoringSettings>>}
     */
    async [ACTION_LOAD_CONTAINER_MONITORINGS_SETTINGS](context, {camerasNumbers}) {
      try {
        const response = await this.getters.ajaxClient.post("/v0/analytics/container_monitoring/settings/", {camera_numbers: camerasNumbers});
        return response.data.results.map(rawContainerMonitoringSettings => new ContainerMonitoringSettings(rawContainerMonitoringSettings));
      } catch {
        return [];
      }
    },
    /**
     * Загрузка настроек аналитики доступа к мусоркам с конкретной камеры.
     *
     * @param {Function} dispatch
     * @param {String} cameraNumber
     * @return {Promise.<ContainerMonitoringSettings>}
     */
    async [ACTION_LOAD_CONTAINER_MONITORING_SETTINGS]({dispatch}, {cameraNumber}) {
      const containerMonitoringsSettings = await dispatch(ACTION_LOAD_CONTAINER_MONITORINGS_SETTINGS, {camerasNumbers: [cameraNumber]});
      if (_.isEmpty(containerMonitoringsSettings)) {
        return null;
      }
      return containerMonitoringsSettings[0];
    },
    /**
     * Загрузка перечня событий по аналитике доступа к мусоркам с камеры для формирования отчета.
     *
     * @param {Object} context
     * @param {String} cameraNumber
     * @param {Array.<Number>} eventIds
     * @return {Promise.<Array.<ContainerMonitoringMessage>>}
     */
    async [ACTION_LOAD_CONTAINER_MONITORING_EVENTS](context, {cameraNumber, eventIds}) {
      try {
        const response = await this.getters.ajaxClient.post("/v0/analytics/container_monitoring/events/", {
          camera_number: cameraNumber,
          event_ids: eventIds,
        });
        return response.data.results.map(rawEventData => makeMessage(rawEventData, cameraNumber, ANALYTICS.container_monitoring));
      } catch {
        return [];
      }
    },
    /**
     * Загрузка перечня событий по аналитике доступа к мусоркам с камеры для формирования отчета.
     *
     * @param {Function} dispatch
     * @param {String} cameraNumber
     * @param {Number} eventId
     * @return {Promise.<ContainerMonitoringMessage>}
     */
    async [ACTION_LOAD_CONTAINER_MONITORING_EVENT]({dispatch}, {cameraNumber, eventId}) {
      const containerMonitoringsSettings = await dispatch(ACTION_LOAD_CONTAINER_MONITORING_EVENTS, {cameraNumber, eventIds: [eventId]});
      if (_.isEmpty(containerMonitoringsSettings)) {
        return null;
      }
      return containerMonitoringsSettings[0];
    },
    /**
     * Загрузка перечня событий по определению доступа к мусоркам с камеры для формирования отчета.
     *
     * @param {Object} context
     * @param {String} cameraNumber
     * @param {Date} archiveFrom
     * @param {Date} archiveTo
     * @param {String} query
     * @return {Promise.<Array.<ContainerMonitoringMessage>>}
     */
    async [ACTION_LOAD_CONTAINER_MONITORING_REPORT](context, {cameraNumber, archiveFrom, archiveTo, query = null, page, page_size, cancelToken}) {
      try {
        const response = await this.getters.ajaxClient.post("/v0/analytics/container_monitoring/report/", {
          camera_number: cameraNumber,
          start: archiveFrom.toISOString(),
          end: archiveTo.toISOString(),
          query: query,
          page: page,
          page_size: page_size
        }, {cancelToken});
        const messages = response.data.results.map(rawEventData => makeMessage(rawEventData, cameraNumber, ANALYTICS.container_monitoring));

        return {
          messages,
          response: response.data
        };
      } catch {
        return {
          messages: [],
          response: null
        };
      }
    },
  },
};
